import React from 'react';
import { register } from 'swiper/element/bundle';
import { makeStyles } from 'tss-react/mui';
import { alpha } from '@mui/material';

import { useIntl } from 'ha/i18n';
import { IconButton, units } from '@hbf/dsl';
import { ArrowButtonLeft, ArrowButtonRight } from '@hbf/icons/streamline-bold';

import { SwiperInstance } from './types';

interface Items {
  key: string | number;
  element: JSX.Element | React.ReactNode;
}
export interface Props {
  setIndexChange?: (index: number) => void;
  withKeyboardControls?: boolean;
  loop?: boolean;
  items: Items[];
  withDefaultControls?: boolean;
  slidesPerView?: number;
  spaceBetween?: number;
  lazy?: boolean;
  swiperContainerStyles?: React.CSSProperties;
  controls?: [JSX.Element | React.ReactNode, JSX.Element | React.ReactNode];
}

const useStyles = makeStyles()(theme => ({
  arrowButtonDisabled: {
    color: `${alpha(theme.palette.white.main, 0.5)} !important`,
  },
  swiperNavButton: {
    color: theme.palette.white.main,
    filter: `drop-shadow(0px 1px 3px ${theme.palette.grey[700]})`,
    position: 'absolute',
    width: units.rem(3),
    height: units.rem(3),
    top: 0,
    bottom: 0,
    margin: 'auto 0',
    zIndex: 10,
  },
  swiperNavButtonPrev: {
    left: 0,
  },
  swiperNavButtonNext: {
    right: 0,
  },
}));

const withControls = (Component: any) => (props: ControlProps) =>
  <Component {...props} />;

interface ControlProps {
  onClick: (action: 'slidePrev' | 'slideNext') => void;
  disabled: boolean;
}

register();

export const SwiperSliderInternal: React.FC<React.PropsWithChildren<Props>> = ({
  items,
  children,
  setIndexChange,
  withKeyboardControls = false,
  loop = false,
  withDefaultControls = false,
  slidesPerView = 1,
  spaceBetween = 0,
  lazy = false,
  swiperContainerStyles,
  controls = [],
}) => {
  const { T } = useIntl();
  const { classes, cx } = useStyles();
  const swiperElRef = React.useRef<any>(null);
  const [swiperInstance, setSwiperInstance] =
    React.useState<null | SwiperInstance>(null);
  const [activeSlideIndex, setActiveSlideIndex] = React.useState<number>(0);

  React.useEffect(() => {
    if (swiperElRef.current) {
      const swiper = swiperElRef?.current?.swiper;
      setSwiperInstance(swiper);
    }
  }, [setIndexChange]);

  React.useEffect(() => {
    if (swiperElRef.current) {
      swiperElRef.current.addEventListener(
        'activeindexchange',
        (event: any) => {
          const [swiper] = event.detail;

          setActiveSlideIndex(swiper.activeIndex);
          if (setIndexChange) {
            setIndexChange(swiper.activeIndex);
          }
        },
      );
    }
  }, [setIndexChange]);

  const handleSlideNavigation = React.useCallback(
    (navigation: 'slidePrev' | 'slideNext') => {
      if (!swiperInstance) {
        return;
      }
      swiperInstance[navigation]();
    },
    [swiperInstance],
  );

  const isFirstSlide = activeSlideIndex === 0;
  const isLastSlide = !items?.length
    ? false
    : activeSlideIndex === items.length - slidesPerView;

  return (
    <React.Fragment>
      {withDefaultControls && (
        <IconButton
          edge="start"
          className={cx(
            classes.swiperNavButton,
            classes.swiperNavButtonPrev,
            isFirstSlide && classes.arrowButtonDisabled,
          )}
          aria-label={T('Previous image')}
          onClick={event => {
            event.preventDefault();
            handleSlideNavigation('slidePrev');
          }}
          data-test-locator="SwiperSlider/Prev button"
        >
          <ArrowButtonLeft />
        </IconButton>
      )}
      <swiper-container
        ref={swiperElRef}
        slides-per-view={slidesPerView}
        loop={loop}
        space-between={spaceBetween}
        data-test-locator="SwiperSlider/container"
        keyboard={withKeyboardControls}
        style={{
          // classes are being ignored so styles need to be inline
          height: '100%',
          width: '100%',
          ...swiperContainerStyles,
        }}
      >
        {items?.map(item => (
          <swiper-slide
            key={item.key}
            lazy={lazy}
            style={{
              // classes are being ignored so styles need to be inline
              height: 'auto',
            }}
            data-test-locator="SwiperSlider/slide"
          >
            {item.element}
          </swiper-slide>
        ))}
      </swiper-container>
      {withDefaultControls && (
        <IconButton
          edge="end"
          className={cx(
            classes.swiperNavButton,
            classes.swiperNavButtonNext,
            isLastSlide && classes.arrowButtonDisabled,
          )}
          onClick={event => {
            event.preventDefault();
            handleSlideNavigation('slideNext');
          }}
          aria-label={T('Next image')}
          data-test-locator="SwiperSlider/Next button"
        >
          <ArrowButtonRight />
        </IconButton>
      )}
      {controls[0] &&
        withControls(controls[0])({
          disabled: !loop && isFirstSlide,
          onClick: () => handleSlideNavigation('slidePrev'),
        })}
      {controls[1] &&
        withControls(controls[1])({
          disabled: !loop && isLastSlide,
          onClick: () => handleSlideNavigation('slideNext'),
        })}
      {children}
    </React.Fragment>
  );
};

const SwiperSlider = React.memo(SwiperSliderInternal);
// eslint-disable-next-line import/no-default-export
export default SwiperSlider;
